import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [expiredName, setExpiredName] = useState(null);
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  // Detect session expiry: a 401 on any non-auth request while logged in
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const isAuthRoute =
          url.includes("/auth/login") || url.includes("/auth/me") || url.includes("/auth/logout");
        const cur = userRef.current;
        if (status === 401 && cur && cur.role && !isAuthRoute) {
          setExpiredName(cur.name || cur.username || "");
          setUser(false);
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    setExpiredName(null);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setExpiredName(null);
      setUser(false);
    }
  };

  const clearExpired = () => setExpiredName(null);

  const readOnly = user?.role === "viewer";

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh: check, expiredName, clearExpired, readOnly }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
